// 주2회권(사장님 결정 2026-10-18): 주(월~일)마다 회원 예약 2회, 시작한 주(목요일 시작이어도)도 2회.
// 첫 주에 1회만 채운 회원은 코치가 회원 상세 [한 주 연장]으로 종료일을 7일 늘리고, 늘어난 주는 1회만 예약된다.
// 서버 규칙의 정본은 cloudapi models.Membership.WeeklyLimitOn, 연장은 POST /v1/admin/memberships/{id}/extend-week다.
// node 단위 테스트가 불러오도록 import 없이 둔다(scripts/test-weekly-pass.mjs).

export const WEEKLY_PASS = "주2회권";

// 수강권 추가 모달의 기간(주). 시작한 주를 1주차로 친 N주차 일요일이 종료일이다
export const WEEKLY_PASS_WEEKS = [4, 8, 12];

// 수강권 추가 모달에서 주2회권을 고르면 보이는 안내. Hyrox·Running·기타 예약은 주 2회에 세지 않는다(사장님 결정 2026-10-24, SD-13·CT-11:
// 서버 CountWeeklyPassBookingsBetween·weeklyLimitReached). 주는 월~일이라 일요일 정규 수업도 센다(WP-1)
export const WEEKLY_PASS_HINT =
  "주(월~일)마다 정규 수업 2회(시작한 주도 2회). Hyrox·Running·기타는 주 2회에 세지 않아요. 첫 주에 1회만 채웠으면 수강권 줄의 [한 주 연장]으로 1회짜리 주를 더할 수 있어요.";

export function isWeeklyPass(m) {
  return !!m && m.type === WEEKLY_PASS;
}

// "YYYY-MM-DD" → UTC 자정 Date (요일 계산만 하므로 시간대 영향이 없게 UTC로 본다). 형식이 틀리면 null
function parseYmd(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(d.getTime()) ? null : d;
}

function ymdOf(d) {
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function addDays(ymd, n) {
  const d = parseYmd(ymd);
  if (!d) return "";
  d.setUTCDate(d.getUTCDate() + n);
  return ymdOf(d);
}

// 시작일이 든 주를 1주차로 친 weeks주차 일요일. 예: 2026-10-22(목) 4주 → 2026-11-15(일). 잘못된 값이면 ""
export function weeklyPassEndDate(startYmd, weeks) {
  const start = parseYmd(startYmd);
  const n = Number(weeks);
  if (!start || !Number.isInteger(n) || n < 1) return "";
  const isoDay = start.getUTCDay() === 0 ? 7 : start.getUTCDay(); // 월 1 … 일 7
  return addDays(startYmd, 7 - isoDay + 7 * (n - 1));
}

// ymd 이후(같은 날 포함) 첫 월요일. 서버는 월요일이 single_week_from 이후인 주를 1회로 본다(models.Membership.WeeklyLimitOn)
function firstMondayFrom(ymd) {
  const d = parseYmd(ymd);
  if (!d) return "";
  const isoDay = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  return addDays(ymd, isoDay === 1 ? 0 : 8 - isoDay);
}

const dot = (ymd) => String(ymd).replace(/-/g, ".");

// 수강권 줄 아래 안내: 연장한 주2회권이면 1회짜리 첫 주의 월요일로 "2026.11.16부터 주 1회", 아니면 "".
// 종료일을 당겨 연장을 무른 뒤 남은 값(그 월요일이 종료일 뒤)이면 보이지 않는다
export function weeklyPassNote(m) {
  if (!isWeeklyPass(m) || !m.single_week_from) return "";
  const monday = firstMondayFrom(String(m.single_week_from).slice(0, 10));
  if (!monday || monday > String(m.expired_at || "").slice(0, 10)) return "";
  return `${dot(monday)}부터 주 1회`;
}

// [한 주 연장]을 보일지: 주2회권이고 취소가 아님(만료된 것도 연장하면 늘어난 끝이 지금 뒤일 때 다시 사용 중이 된다)
export function canExtendWeek(m) {
  return isWeeklyPass(m) && m.status !== "cancelled";
}

// [한 주 연장] 확인 창 문장. 종료일 m.expired_at("YYYY-MM-DD")을 7일 늘린다. 1회만 되는 주는 늘어난 날 뒤 첫 월요일부터다
// (종료일이 일요일이면 그다음 날. 휴회 등으로 주 중간에 끝나면 그 주는 2회 그대로이고 다음 월요일부터 1회)
export function extendWeekConfirmText(m) {
  const end = String((m && m.expired_at) || "").slice(0, 10);
  const next = addDays(end, 7);
  if (!next) return "한 주 연장할까요? 늘어난 주는 1회만 예약할 수 있어요.";
  const monday = firstMondayFrom(addDays(end, 1));
  return `종료일을 ${dot(end)} → ${dot(next)}로 7일 늘려요. ${dot(
    monday
  )}부터 시작하는 주는 회원이 1회만 예약할 수 있어요. 연장할까요?`;
}

// [한 주 연장] 요청 본문: 확인 창에 보인 종료일. 서버는 잠근 행의 종료일과 다르면 409로 늘리지 않는다
export function extendWeekBody(m) {
  return {
    expected_expired_at: String((m && m.expired_at) || "").slice(0, 10),
  };
}
